import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Briefcase, GraduationCap, MapPin, Award } from 'lucide-react';
import { attorneys, paralegals } from '../data/team';
import { SEO } from '../components/SEO';

export const Attorneys = ({ onOpenLeadPopup }) => {
  return (
    <div className="page-wrapper" data-testid="attorneys-page">
      <SEO 
        title="Our Attorneys"
        description="Meet the experienced attorneys and paralegals at Legal Emphasis. Our team brings expertise in corporate law, intellectual property, immigration, litigation, and government contracting."
        keywords="attorneys, lawyers, paralegals, legal team, San Francisco attorneys, business lawyers"
      />
      
      {/* Hero Section */}
      <section className="bg-[#0A2A5E] py-20 md:py-32" data-testid="attorneys-hero">
        <div className="le-container">
          <div className="max-w-3xl">
            <h1 className="le-heading-1 text-white mb-6">Our Legal Team</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Meet the experienced attorneys and dedicated professionals who make Legal Emphasis a trusted partner for businesses nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* Attorneys Section */}
      <section className="le-section bg-white" data-testid="attorneys-list">
        <div className="le-container">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="le-heading-2 mb-4">Our Attorneys</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our attorneys bring diverse backgrounds and deep expertise across multiple practice areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {attorneys.map((attorney) => {
              const IconComponent = attorney.icon;
              return (
                <div 
                  key={attorney.id}
                  className="le-card group"
                  data-testid={`attorney-card-${attorney.id}`}
                >
                  <div className="text-center mb-6">
                    {/* Icon Avatar */}
                    <div className="w-24 h-24 flex items-center justify-center bg-[#F1F5F9] text-[#0A2A5E] mx-auto mb-4 group-hover:bg-[#0A2A5E] group-hover:text-white transition-colors">
                      <IconComponent size={40} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A2A5E] mb-1">{attorney.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{attorney.title}</p>
                    <p className="text-sm text-[#0A2A5E] font-medium">{attorney.specialization}</p>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{attorney.bio}</p>

                  {attorney.focusAreas && (
                    <p className="text-gray-500 text-xs mb-6 leading-relaxed italic">
                      <span className="font-semibold text-[#0A2A5E] not-italic">Focus:</span> {attorney.focusAreas}
                    </p>
                  )}

                  <div className="space-y-3 text-sm border-t border-[#E2E8F0] pt-6">
                    <div className="flex items-start gap-3">
                      <GraduationCap size={16} className="text-[#0A2A5E] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{attorney.education}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="text-[#0A2A5E] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">Bar: {attorney.barAdmissions.join(', ')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Briefcase size={16} className="text-[#0A2A5E] mt-0.5 flex-shrink-0" />
                      <div className="flex flex-wrap gap-1">
                        {attorney.expertise.map((exp, i) => (
                          <span key={i} className="text-xs bg-[#F8F9FA] text-[#0A2A5E] px-2 py-1">
                            {exp}
                          </span>
                        ))}
                      </div>
                    </div>
                    {attorney.memberships && (
                      <div className="flex items-start gap-3">
                        <Award size={16} className="text-[#0A2A5E] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-500 text-xs">{attorney.memberships.join(' · ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Paralegals Section */}
      <section className="le-section bg-[#F8F9FA]" data-testid="paralegals-list">
        <div className="le-container">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="le-heading-2 mb-4">Our Paralegals</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our skilled paralegals provide essential support, ensuring efficient and thorough handling of every matter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paralegals.map((paralegal) => {
              const IconComponent = paralegal.icon;
              return (
                <div 
                  key={paralegal.id}
                  className="bg-white p-8 border border-[#E2E8F0] group hover:border-[#0A2A5E] transition-colors"
                  data-testid={`paralegal-card-${paralegal.id}`}
                >
                  <div className="text-center mb-6">
                    {/* Icon Avatar */}
                    <div className="w-20 h-20 flex items-center justify-center bg-[#F1F5F9] text-[#0A2A5E] mx-auto mb-4 group-hover:bg-[#0A2A5E] group-hover:text-white transition-colors">
                      <IconComponent size={32} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0A2A5E] mb-1">{paralegal.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{paralegal.title}</p>
                    <p className="text-sm text-[#0A2A5E] font-medium">{paralegal.specialization}</p>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{paralegal.bio}</p>

                  <div className="text-sm border-t border-[#E2E8F0] pt-4 space-y-3">
                    <p className="text-gray-500 italic">{paralegal.experience}</p>
                    {paralegal.skills && (
                      <div className="flex flex-wrap gap-1">
                        {paralegal.skills.map((skill, i) => (
                          <span key={i} className="text-xs bg-[#0A2A5E]/5 text-[#0A2A5E] px-2 py-1 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {paralegal.certifications.map((cert, i) => (
                        <span key={i} className="text-xs bg-[#F8F9FA] text-gray-600 px-2 py-1">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
};
